import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { describeError } from "@/lib/api";
import { Panel, Skeleton, ErrorState, Empty, Pager, Badge, day, num, bytes, selectClass } from "@/components/platform/ui";

const BLANK = { search: "", type: "", plan: "", status: "", start: "", end: "" };

export default function PlatformOrganizations() {
  const [f, setF] = useState(BLANK);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const params = { page, page_size: pageSize };
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    try {
      const { data } = await api.get("/platform/organizations", { params });
      setData(data);
    } catch (e) {
      setErr(describeError(e, "Could not load organisations."));
    } finally {
      setLoading(false);
    }
  }, [f, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setPage(1); setF((p) => ({ ...p, [k]: e.target.value })); };

  return (
    <div data-testid="owner-organizations" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">Organizations</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every firm and advocate on the platform.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <input value={f.search} onChange={set("search")} placeholder="Search by name"
               data-testid="org-search-input" className={`${selectClass} lg:col-span-2`} />
        <select value={f.type} onChange={set("type")} data-testid="org-type-filter" className={selectClass}>
          <option value="">All types</option>
          <option value="individual">Individual</option>
          <option value="law_firm">Law firm</option>
        </select>
        <select value={f.plan} onChange={set("plan")} data-testid="org-plan-filter" className={selectClass}>
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro Advocate</option>
          <option value="law_firm">Law Firm</option>
        </select>
        <select value={f.status} onChange={set("status")} data-testid="org-status-filter" className={selectClass}>
          <option value="">All statuses</option>
          {["trialing", "active", "cancelled", "expired", "past_due", "suspended"].map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
        <select value={pageSize} data-testid="org-page-size" className={selectClass}
                onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
          {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <input type="date" value={f.start} onChange={set("start")} data-testid="org-start-filter" className={selectClass} />
        <input type="date" value={f.end} onChange={set("end")} data-testid="org-end-filter" className={selectClass} />
        <button type="button" onClick={() => { setF(BLANK); setPage(1); }} data-testid="org-clear-filters"
                className="rounded-md border border-border bg-white px-3 py-2 text-sm text-navy hover:bg-secondary">
          Clear
        </button>
      </div>

      <Panel title="All organizations" testid="owner-org-panel">
        {loading ? <Skeleton rows={6} testid="org-loading" />
          : err ? <ErrorState message={err} onRetry={load} testid="org-error" />
          : !data.items.length ? <Empty message="No organisations match these filters." testid="org-empty" />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm" data-testid="org-table">
                  <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      {["Organization", "Type", "Plan", "Status", "Created", "Usage"].map((h) => (
                        <th key={h} className="px-5 py-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.items.map((o) => (
                      <tr key={o.id} data-testid={`org-row-${o.id}`} className="hover:bg-secondary/40">
                        <td className="px-5 py-3">
                          <Link to={`/platform/organizations/${o.id}`} data-testid={`org-link-${o.id}`}
                                className="font-medium text-navy hover:text-gold-dark">
                            {o.name}
                          </Link>
                        </td>
                        <td className="px-5 py-3 text-muted-foreground">
                          {o.type === "law_firm" ? "Law firm" : "Individual"}
                        </td>
                        <td className="px-5 py-3"><Badge>{o.subscription?.plan}</Badge></td>
                        <td className="px-5 py-3 text-muted-foreground">
                          {(o.subscription?.status || "—").replace("_", " ")}
                        </td>
                        <td className="px-5 py-3 text-muted-foreground">{day(o.created_at)}</td>
                        <td className="px-5 py-3 text-xs text-muted-foreground">
                          {num(o.usage?.cases)} cases · {num(o.usage?.clients)} clients ·{" "}
                          {bytes(o.usage?.storage_bytes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} testid="org-pager" />
            </>
          )}
      </Panel>
    </div>
  );
}
